<template>
  <div class="export-clock-entries">

    <!-- ── BOTONES DE ACCIÓN ── -->
    <div class="action-buttons mb-5">
      <v-btn
        large
        block
        class="white--text"
        :rounded="$getPrimaryButton('rounded')"
        :color="$getPrimaryButton('color')"
        :disabled="!previewData || previewData.length === 0"
        :loading="loadingExport"
        @click="exportFichajes"
      >
        <v-icon left>mdi-microsoft-excel</v-icon>
        Exportar Excel
      </v-btn>
    </div>

    <!-- ── PANEL DE PREVISUALIZACIÓN ── -->
    <transition name="fade-slide">
      <div v-if="previewData && previewData.length > 0" class="preview-panel mt-6">
        <div class="preview-panel--header mb-3 d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon :color="$getColor('vuetifyColor1')" class="mr-2">mdi-table-eye</v-icon>
            <span class="text-subtitle-1 font-weight-bold">Previsualización</span>
            <v-chip small class="ml-3" :color="$getPrimaryButton('color')" dark>
              {{ totalEntries }} fichajes · {{ previewData.length }} trabajadores
            </v-chip>
          </div>
          <div class="d-flex align-center">
            <v-text-field
              v-model="searchWorker"
              placeholder="Buscar trabajador..."
              prepend-inner-icon="mdi-magnify"
              outlined
              dense
              hide-details
              clearable
              style="max-width: 220px;"
              class="mr-3"
            ></v-text-field>
            <v-btn icon small @click="previewData = null; clockEntriesRaw = null">
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </div>
        </div>

        <!-- Resumen de alertas globales -->
        <v-alert v-if="totalAlerts > 0" type="warning" dense text class="mb-4">
          <strong>{{ totalAlerts }} alertas</strong> detectadas en los registros del periodo seleccionado. Revisa los detalles antes de exportar.
        </v-alert>

        <!-- Acordeón por trabajador -->
        <v-expansion-panels accordion focusable>
          <v-expansion-panel
            v-for="worker in filteredPreviewData"
            :key="worker.workerId"
            class="preview-worker-panel"
          >
            <v-expansion-panel-header hide-actions>
              <template v-slot:default="{ open }">
                <div class="worker-header">
                  <!-- Izquierda: avatar + nombre -->
                  <div class="worker-header--identity">
                    <v-avatar :color="$getColor('vuetifyColor1')" size="32" class="mr-3 flex-shrink-0">
                      <span class="white--text text-caption font-weight-bold">{{ workerInitials(worker.workerName, worker.workerLastName) }}</span>
                    </v-avatar>
                    <div>
                      <div class="font-weight-medium text-body-2">{{ worker.workerName }} {{ worker.workerLastName }}</div>
                      <div class="text-caption text--secondary">{{ worker.workerEmail }}</div>
                    </div>
                  </div>
                  <!-- Derecha: chips + chevron -->
                  <div class="worker-header--meta">
                    <v-chip x-small class="mr-1" outlined>
                      <v-icon x-small left>mdi-calendar-clock</v-icon>
                      {{ worker.groupedEntries.length }} días
                    </v-chip>
                    <v-chip x-small class="mr-1" :color="$getPrimaryButton('color')" dark>
                      <v-icon x-small left>mdi-clock-outline</v-icon>
                      {{ totalWorkerHours(worker.groupedEntries) }}h
                    </v-chip>
                    <v-chip x-small color="warning" dark class="mr-2" v-if="workerAlerts(worker.groupedEntries) > 0">
                      <v-icon x-small left>mdi-alert</v-icon>
                      {{ workerAlerts(worker.groupedEntries) }} alertas
                    </v-chip>
                    <v-icon small :style="{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }">
                      mdi-chevron-down
                    </v-icon>
                  </div>
                </div>
              </template>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-simple-table dense class="preview-table mt-2">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Fichajes</th>
                    <th>Total trabajado</th>
                    <th>Alertas</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="day in worker.groupedEntries" :key="day.time"
                      :class="{ 'row-alert': dayHasAlerts(day) }">
                    <td class="text-caption">{{ day.stringTime }}</td>
                    <td>
                      <div class="d-flex flex-wrap gap-1">
                        <v-chip
                          v-for="(entry, idx) in day.entries"
                          :key="idx"
                          x-small
                          :color="entry.sourceType === 'CLOCK_IN' ? 'success' : 'error'"
                          dark
                          class="mr-1 mb-1"
                        >
                          <v-icon x-small left>{{ entry.sourceType === 'CLOCK_IN' ? 'mdi-login' : 'mdi-logout' }}</v-icon>
                          {{ trimTime(entry.timeLocale) }}
                        </v-chip>
                      </div>
                    </td>
                    <td>
                      <span :class="day.alertDayWithUnequalEntries ? 'error--text' : ''">
                        {{ day.stringWorkTime || '—' }}
                      </span>
                    </td>
                    <td>
                      <div v-if="dayHasAlerts(day)" class="d-flex flex-wrap gap-1">
                        <v-tooltip bottom v-if="day.alertDayStartWithClockOut || day.alertDayEndWithClockIn">
                          <template v-slot:activator="{ on }"><v-icon small color="warning" v-on="on">mdi-swap-vertical</v-icon></template>
                          <span>Día comienza con salida o termina con entrada</span>
                        </v-tooltip>
                        <v-tooltip bottom v-if="day.alertDayWithUnequalEntries">
                          <template v-slot:activator="{ on }"><v-icon small color="error" v-on="on">mdi-scale-unbalanced</v-icon></template>
                          <span>Entradas y salidas desiguales</span>
                        </v-tooltip>
                        <v-tooltip bottom v-if="day.alertDayWithManualEntries">
                          <template v-slot:activator="{ on }"><v-icon small color="info" v-on="on">mdi-pencil</v-icon></template>
                          <span>Fichaje manual de usuario</span>
                        </v-tooltip>
                        <v-tooltip bottom v-if="day.alertDayWithAdminEntries">
                          <template v-slot:activator="{ on }"><v-icon small color="deep-purple" v-on="on">mdi-shield-edit</v-icon></template>
                          <span>Fichaje manual de administrador</span>
                        </v-tooltip>
                        <v-tooltip bottom v-if="day.alertDayWithConsecutivesSameTypes">
                          <template v-slot:activator="{ on }"><v-icon small color="orange" v-on="on">mdi-repeat</v-icon></template>
                          <span>Fichajes consecutivos del mismo tipo</span>
                        </v-tooltip>
                      </div>
                      <span v-else class="text--disabled text-caption">—</span>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>
    </transition>

    <!-- Estado vacío tras previsualizar -->
    <transition name="fade-slide">
      <div v-if="previewEmpty" class="empty-state mt-6 d-flex flex-column align-center">
        <v-icon size="64" color="grey lighten-2">mdi-calendar-remove-outline</v-icon>
        <p class="text--secondary mt-3">No se encontraron fichajes para el periodo seleccionado.</p>
      </div>
    </transition>

  </div>
</template>

<script>
import clockEntryService from '@/services/clockentry.service';

export default {
  name: 'export-clock-entries',
  props: {
    externalDateFrom: { type: String, default: null },
    externalDateTo: { type: String, default: null },
  },
  data() {
    return {
      // Selector mode
      selectorMode: 'month',

      // Month picker (modo original)
      selectedMonthPicker: null,

      // Range picker (modo nuevo)
      selectedRange: [],

      // State
      workcenterId: null,
      workcenterName: null,

      loadingPreview: false,
      loadingExport: false,

      clockEntriesRaw: null,
      previewData: null,
      previewEmpty: false,
      searchWorker: '',
    };
  },
  mounted() {
    if (this.$store.state.projectId === null) {
      this.$router.push({ name: 'SelectWorkCenter' });
    } else {
      this.workcenterId = this.$store.state.projectId;
      this.workcenterName = this.$store.state.project;
    }
  },
  computed: {
    canPreview() {
      if (this.externalDateFrom) return !!(this.externalDateFrom && this.externalDateTo);
      if (this.selectorMode === 'month') return !!this.selectedMonthPicker;
      return this.selectedRange && this.selectedRange.length === 2;
    },
    filteredPreviewData() {
      if (!this.previewData) return [];
      if (!this.searchWorker) return this.previewData;
      const q = this.searchWorker.toLowerCase();
      return this.previewData.filter((w) => `${w.workerName} ${w.workerLastName} ${w.workerEmail}`.toLowerCase().includes(q));
    },
    totalEntries() {
      if (!this.clockEntriesRaw) return 0;
      return this.clockEntriesRaw.length;
    },
    totalAlerts() {
      if (!this.previewData) return 0;
      return this.previewData.reduce((acc, w) => acc + this.workerAlerts(w.groupedEntries), 0);
    },
  },
  methods: {
    getDateRange() {
      // Prioriza el rango externo si se recibe como prop
      if (this.externalDateFrom && this.externalDateTo) {
        return { dateFrom: this.externalDateFrom, dateTo: this.externalDateTo };
      }
      if (this.selectorMode === 'month') {
        return clockEntryService.getDateTimeRange(this.selectedMonthPicker, 'YYYY-MM');
      }
      const sorted = [...this.selectedRange].sort();
      return clockEntryService.getDateTimeRange(sorted, '[YYYY-MM-DD,YYYY-MM-DD]');
    },

    async loadPreview() {
      this.loadingPreview = true;
      this.previewData = null;
      this.previewEmpty = false;
      this.clockEntriesRaw = null;

      const dateRange = this.getDateRange();
      try {
        const response = await clockEntryService.getWorkersClockEntriesInRange(this.workcenterId, dateRange.dateFrom, dateRange.dateTo);

        // Transformar a formato con timeLocale igual que exportClockEntries
        this.clockEntriesRaw = response.map((item) => ({
          ...item,
          time: clockEntryService.unixTimeToDateTime(item.unixClockInTime),
          timeLocale: clockEntryService.unixTimeToLocaleDateTime(item.unixClockInTime),
          type: item.type ? this.$getclockEntryTypeValues(item.type).label : 'N/A',
          workerName: item.worker.name,
          workerLastName: item.worker.lastName1,
          workerEmail: item.worker.email,
          workerId: item.worker.id,
          worker: item.worker,
          sourceType: item.type,
        }));

        if (this.clockEntriesRaw.length === 0) {
          this.previewEmpty = true;
        } else {
          // Agrupar por trabajador usando el servicio
          const grouped = clockEntryService.getGroupedEntriesForAllWorkers(this.clockEntriesRaw);
          this.previewData = Object.values(grouped).map((wd) => ({
            workerId: wd.worker.id,
            workerName: wd.worker.name,
            workerLastName: wd.worker.lastName1,
            workerEmail: wd.worker.email,
            groupedEntries: wd.groupedEntries,
          }));
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.loadingPreview = false;
      }
    },

    exportFichajes() {
      if (!this.clockEntriesRaw) return;
      this.loadingExport = true;
      // Reutilizar la misma lógica de descarga del servicio
      const eventsCl = this.clockEntriesRaw.map((item) => ({
        id: item.id,
        date: item.clockInTime,
        unixClockInTime: item.unixClockInTime,
        sourceType: item.type,
        manualEntry: item.manualEntry,
        adminEntry: item.adminEntry,
        time: item.time,
        timeLocale: item.timeLocale,
        type: item.type ? this.$getclockEntryTypeValues(item.sourceType)?.label || item.type : 'N/A',
        workerId: item.worker.id,
        workerName: item.worker.name,
        workerLastName: item.worker.lastName1,
        workerEmail: item.worker.email,
        worker: item.worker,
        latitude: item.latitude,
        longitude: item.longitude,
        errorLocation: item.errorLocation,
      }));
      clockEntryService.downloadClockEntriesForAllWorkers(eventsCl);
      this.loadingExport = false;
    },

    // ── Helpers de previsualización ──
    workerInitials(name, lastName) {
      return `${(name || '').charAt(0)}${(lastName || '').charAt(0)}`.toUpperCase();
    },
    totalWorkerHours(groupedEntries) {
      const total = groupedEntries.reduce((acc, d) => acc + (d.workTime || 0), 0);
      return total.toFixed(1);
    },
    workerAlerts(groupedEntries) {
      return groupedEntries.filter((d) => this.dayHasAlerts(d)).length;
    },
    dayHasAlerts(day) {
      return day.alertDayStartWithClockOut
        || day.alertDayEndWithClockIn
        || day.alertDayWithManualEntries
        || day.alertDayWithAdminEntries
        || day.alertDayWithUnequalEntries
        || day.alertDayWithConsecutivesSameTypes;
    },
    trimTime(localeStr) {
      if (!localeStr) return '';
      const parts = localeStr.split(',');
      return parts.length > 1 ? parts[1].trim() : localeStr;
    },
  },
  watch: {
    selectedMonthPicker(val) {
      if (val && this.selectorMode === 'month') this.loadPreview();
    },
    selectedRange: {
      handler(val) {
        if (val && val.length === 2 && this.selectorMode === 'range') this.loadPreview();
      },
      deep: true,
    },
    selectorMode() {
      this.previewData = null;
      this.clockEntriesRaw = null;
      this.previewEmpty = false;
    },
    externalDateFrom(val) {
      if (val && this.externalDateTo) this.loadPreview();
    },
    externalDateTo(val) {
      if (val && this.externalDateFrom) this.loadPreview();
    },
  },
};
</script>

<style scoped lang="scss">
.export-clock-entries {
  width: 100%;
}

/* ── Layout responsivo ── */
.ece-layout {
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: flex-start;
    gap: 0;
  }
}

.ece-col-left {
  flex: 0 0 auto;
  width: 100%;

  @media (min-width: 768px) {
    width: 340px;
    position: sticky;
    top: 16px;
    padding-right: 40px;
    margin-right: 16px;
    border-right: 2px solid rgba(128, 128, 128, 0.15);
  }
}

.ece-col-right {
  flex: 1 1 0;
  min-width: 0;
}

// Elimina el padding interno de Vuetify para que la tabla ocupe todo el ancho
::v-deep .v-expansion-panel-content__wrap {
  padding: 0 !important;
}

.worker-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 8px;
  min-width: 0;

  &--identity {
    display: flex;
    align-items: center;
    flex: 2 1 0;
    min-width: 0;

    div {
      min-width: 0;
    }
  }

  &--meta {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    flex-wrap: nowrap;
    gap: 4px;
  }
}

.selector-mode-toggle {
  display: flex;
  justify-content: center;
}

.picker-section {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.range-picker-wrapper {
  width: 100%;
  max-width: 360px;
}

.range-summary {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}

.action-buttons {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
}

.preview-panel {
  &--header {
    flex-wrap: wrap;
    gap: 8px;
  }
}

.preview-worker-panel {
  margin-bottom: 4px;
}

.preview-table {
  width: 100% !important;
  th {
    font-size: 11px !important;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    opacity: 0.7;
    white-space: nowrap;
  }
  td {
    font-size: 12px !important;
    vertical-align: middle;
    padding-top: 6px !important;
    padding-bottom: 6px !important;
  }
}

.row-alert {
  background-color: rgba(255, 152, 0, 0.06);
}

.empty-state {
  padding: calc(var(--base) * 4) 0;
  text-align: center;
}

// Transición
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.28s ease;
}
.fade-slide-enter,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(8px);
}
</style>
